import React, { Fragment } from 'react';

import PropTypes from 'prop-types';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import AdditionalTrainingBar from 'components/training/AdditionalTrainingBar';
import AdditionalTrainingTable from 'components/training/AdditionalTrainingTable';
import RoleTrainingBar from 'components/training/RoleTrainingBar';
import RoleTrainingTable from 'components/training/RoleTrainingTable';

import AddNotedIcon from '-!svg-react-loader?name=VisibleIcon!icons/add-notes.svg';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function RoleTrainingTab(props) {
  const currentActor = useCurrentActor();

  const personnelRole = props.role || {
    id: 'blankRole',
    relationships: { courses: { data: [] } },
  };

  const tabCompanyRoleCourseIds =
    personnelRole.relationships.courses ?
      personnelRole.relationships.courses.data.map(
        (companyRoleCourse) => companyRoleCourse.id,
      )
    : [];
  const allCompanyRoleCourseIds = props.roles.reduce((acc, role) => {
    const ids = role.relationships.courses.data.map(
      (roleCourse) => roleCourse.id,
    );
    return [...acc, ...ids];
  }, []);
  const allUserCourseCourseIds = props.userCourses.map(
    (userCourse) => userCourse.relationships.course.data.id,
  );

  const nonAssociatedTraining = props.training.filter(
    (training) =>
      ![...allUserCourseCourseIds, ...allCompanyRoleCourseIds].includes(
        training.relationships.course.data.id,
      ),
  );
  const nonAssociatedTrainingCourseIds = nonAssociatedTraining.map(
    (training) => training.relationships.course.data.id,
  );

  const bookingCourseIds = props.currentBookings.map(
    (booking) => booking.relationships.course.data.id,
  );
  const tabCompanyRoleRequiredCourses = tabCompanyRoleCourseIds.map(
    (id) => props.courses[id],
  );
  const additionalCourses = [
    ...new Set([
      ...allUserCourseCourseIds,
      ...allCompanyRoleCourseIds,
      ...nonAssociatedTrainingCourseIds,
      ...bookingCourseIds,
    ]),
  ]
    .filter((id) => !tabCompanyRoleCourseIds.includes(id))
    .map((id) => props.courses[id]);
  const roleHasRequiredCourses = tabCompanyRoleRequiredCourses.length > 0;

  const courseCompanyRolesForRole = props.courseCompanyRoles.filter(
    (ccr) => ccr.relationships.companyRole.data.id === props.role.id,
  );
  const filteredCourseCompanyRoles = courseCompanyRolesForRole.reduce(
    (acc, ccr) => {
      acc[ccr.relationships.course.data.id] = ccr;
      return acc;
    },
    {},
  );

  if (
    currentActor.user.attributes.accessType === 'personnel' &&
    tabCompanyRoleRequiredCourses.length === 0 &&
    additionalCourses.length === 0
  ) {
    return (
      <div className='tw-flex tw-h-90 tw-flex-col tw-items-center tw-justify-center tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
        <AddNotedIcon className='-tw-mt-3' height={92} width={92} />
        <div className='tw-mt-3 tw-text-center tw-text-l tw-font-semibold tw-text-grey-900'>
          No training added
        </div>
        <div className='tw-mt-4 tw-text-center tw-text-grey-700'>
          Contact your manager to request training to be added.
        </div>
      </div>
    );
  }

  return (
    <>
      <RoleTrainingBar
        isArchived={props.isArchived}
        name={props.currentPersonnel.firstName}
        onAddRole={props.onAddRole}
        onTrainingEvidenceProcessed={props.onTrainingEvidenceProcessed}
        personnelId={props.currentPersonnel.id}
        role={props.role}
        roleHasRequiredCourses={roleHasRequiredCourses}
        training={props.training}
        trainingEvidenceProcessed={props.trainingEvidenceProcessed}
        trainingStatusReport={props.trainingStatusReport}
      />
      {roleHasRequiredCourses && (
        <RoleTrainingTable
          attachments={props.attachments}
          autoEnrolmentConfigurations={props.autoEnrolmentConfigurations}
          autoEnrolmentExclusions={props.autoEnrolmentExclusions}
          bookers={props.bookers}
          courseCompanyRoles={filteredCourseCompanyRoles}
          courses={tabCompanyRoleRequiredCourses}
          creditInfoUnavailable={props.creditInfoUnavailable}
          currentBookings={props.currentBookings}
          currentPersonnel={props.currentPersonnel}
          currentRegistrations={props.currentRegistrations}
          eLearningAllowance={props.eLearningAllowance}
          eLearningCourses={props.eLearningCourses}
          hasTrainingEditableAccess={props.hasTrainingEditableAccess}
          onAddBooking={props.onAddBooking}
          onAddELearningBooking={props.onAddELearningBooking}
          onBookingReminderClick={props.onBookingReminderClick}
          onCreateAutoEnrolmentExclusion={props.onCreateAutoEnrolmentExclusion}
          onDeleteAutoEnrolmentExclusion={props.onDeleteAutoEnrolmentExclusion}
          onDeleteTrainingClick={props.onDeleteTrainingClick}
          onEditTrainingClick={props.onEditTrainingClick}
          onNewTrainingClick={props.onNewTrainingClick}
          onRemoveBooking={props.onRemoveBooking}
          onShowBooking={props.onShowBooking}
          personnelId={props.personnelId}
          setModalBoxData={props.setModalBoxData}
          training={props.training}
          userCourses={props.userCourses}
        />
      )}
      {((props.isArchived && additionalCourses.length > 0) ||
        !props.isArchived) && (
        <div className='m-t-16'>
          {((currentActor.user.attributes.accessType === 'personnel' &&
            additionalCourses.length > 0) ||
            currentActor.user.attributes.accessType !== 'personnel') && (
            <AdditionalTrainingBar
              actionButtonsVisible={
                props.hasTrainingEditableAccess && !props.isArchived
              }
              additionalCoursesCount={additionalCourses.length}
              courses={props.courses}
              coursesMeta={props.coursesMeta}
              onCourseSelectChange={props.onCourseSelectChange}
              onCourseSelectError={props.onCourseSelectError}
              personnelId={props.currentPersonnel.id}
              roleHasRequiredCourses={roleHasRequiredCourses}
              roles={props.roles}
              training={props.training}
              userCourses={props.userCourses}
            />
          )}
          {additionalCourses.length > 0 && (
            <AdditionalTrainingTable
              attachments={props.attachments}
              autoEnrolmentConfigurations={props.autoEnrolmentConfigurations}
              autoEnrolmentExclusions={props.autoEnrolmentExclusions}
              bookers={props.bookers}
              courses={additionalCourses}
              creditInfoUnavailable={props.creditInfoUnavailable}
              currentBookings={props.currentBookings}
              currentPersonnel={props.currentPersonnel}
              currentRegistrations={props.currentRegistrations}
              eLearningAllowance={props.eLearningAllowance}
              eLearningCourses={props.eLearningCourses}
              hasTrainingEditableAccess={props.hasTrainingEditableAccess}
              onAddBooking={props.onAddBooking}
              onAddELearningBooking={props.onAddELearningBooking}
              onBookingReminderClick={props.onBookingReminderClick}
              onCreateAutoEnrolmentExclusion={
                props.onCreateAutoEnrolmentExclusion
              }
              onCreateUserCourse={props.onCreateUserCourse}
              onDeleteAutoEnrolmentExclusion={
                props.onDeleteAutoEnrolmentExclusion
              }
              onDeleteCourseClick={props.onDeleteCourseClick}
              onDeleteTrainingClick={props.onDeleteTrainingClick}
              onEditTrainingClick={props.onEditTrainingClick}
              onNewTrainingClick={props.onNewTrainingClick}
              onRemoveBooking={props.onRemoveBooking}
              onShowBooking={props.onShowBooking}
              onUpdateUserCourse={props.onUpdateUserCourse}
              personnelId={props.personnelId}
              roles={props.roles}
              setModalBoxData={props.setModalBoxData}
              training={props.training}
              userCourses={props.userCourses}
            />
          )}
        </div>
      )}
    </>
  );
}

RoleTrainingTab.propTypes = {
  currentPersonnel: PropTypes.object.isRequired,
  currentBookings: PropTypes.array,
  currentRegistrations: PropTypes.array,
  training: PropTypes.array.isRequired,
  bookers: PropTypes.object,
  roles: PropTypes.array.isRequired,
  attachments: PropTypes.array.isRequired,
  role: PropTypes.object,
  courses: PropTypes.object,
  autoEnrolmentConfigurations: PropTypes.object,
  autoEnrolmentExclusions: PropTypes.object,
  eLearningCourses: PropTypes.object,
  eLearningAllowance: PropTypes.object,
  courseCompanyRoles: PropTypes.array,
  userCourses: PropTypes.array,
  hasTrainingEditableAccess: PropTypes.bool,
  creditInfoUnavailable: PropTypes.bool,
  onBookingReminderClick: PropTypes.func.isRequired,
  onShowBooking: PropTypes.func.isRequired,
  onRemoveBooking: PropTypes.func.isRequired,
  onCreateAutoEnrolmentExclusion: PropTypes.func,
  onDeleteAutoEnrolmentExclusion: PropTypes.func,
  isArchived: PropTypes.bool,
  onAddRole: PropTypes.func,
  trainingEvidenceProcessed: PropTypes.bool,
  onTrainingEvidenceProcessed: PropTypes.func,
  onNewTrainingClick: PropTypes.func,
  onEditTrainingClick: PropTypes.func,
  onDeleteTrainingClick: PropTypes.func,
  onAddBooking: PropTypes.func,
  onAddELearningBooking: PropTypes.func,
  setModalBoxData: PropTypes.func,
  personnelId: PropTypes.string,
  coursesMeta: PropTypes.object,
  onCourseSelectChange: PropTypes.func,
  onCourseSelectError: PropTypes.func,
  onUpdateUserCourse: PropTypes.func,
  onCreateUserCourse: PropTypes.func,
  onDeleteCourseClick: PropTypes.func,
  statusReport: PropTypes.shape({
    courses: PropTypes.arrayOf(resourceShape('courses')),
    status: PropTypes.string,
  }),
};

RoleTrainingTab.defaultProps = {
  userCourses: [],
};
